import { ReactNode, useCallback } from "react";

import { AppRoute } from "../../../constants.ts";
import { RolePermission } from "../../../graphql/generated.ts";
import { useTenantId } from "../../../hooks/useTenantId.ts";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { canViewCreditLimitMenu } from "../../../utils/permissions.ts";
import { hasPermission, isBababosSuperAdmin } from "../../../utils/user.ts";
import SvgAIIcon from "../../Icons/AIIcon.tsx";
import SvgCreditCard from "../../Icons/CreditCard.tsx";
import SvgFile from "../../Icons/File.tsx";
import SvgFolder from "../../Icons/Folder.tsx";
import KYCIcon from "../../Icons/KYC.tsx";
import SvgSearch from "../../Icons/Search.tsx";
import SvgSettings from "../../Icons/Settings.tsx";
import SvgUser from "../../Icons/User.tsx";
import SvgUserScan from "../../Icons/UserScan.tsx";
import SidebarItem from "../Sidebar/SidebarItem/SidebarItem.tsx";
import { NavigationLink } from "../types.ts";

interface Props {
  isOpen?: boolean;
  onClick?: () => void;
  iconSize?: number;
  renderer?: (link: NavigationLink, index: number) => ReactNode;
}

export function useNavigationLinks({
  isOpen = true,
  onClick,
  iconSize = 30,
  renderer,
}: Props) {
  const { tenantId } = useTenantId();
  const { user } = useCurrentUserContext();

  const superAdmin = isBababosSuperAdmin(user);

  const links: NavigationLink[] = [
    {
      label: "Settings",
      icon: <SvgSettings width={iconSize} height={iconSize} />,
      nestedLinks: [
        {
          label: "User Access",
          url: AppRoute.UserAccessSetting,
          show: hasPermission(user, RolePermission.CanViewUserAccessSetting),
        },
        {
          label: "Tenant Management",
          url: AppRoute.TenantManagement,
          show: hasPermission(
            user,
            RolePermission.CanViewUserAccessSetting,
            true
          ),
        },
        {
          label: "Prompt Editor",
          url: AppRoute.PropmtEditor,
          show: hasPermission(user, RolePermission.CanViewPrompt, true),
        },
        {
          label: "Profile Settings",
          url: AppRoute.ProfileSetting,
          show:
            !superAdmin || (superAdmin && user?.tenantId === Number(tenantId)),
        },
      ],
    },
    {
      label: "Customer Database",
      icon: <SvgUser width={iconSize} height={iconSize} />,
      url: AppRoute.Customers,
      show: hasPermission(user, RolePermission.CanViewCustomerDatabase),
    },
    {
      label: "Credit Risk",
      icon: <SvgCreditCard width={iconSize} height={iconSize} />,
      show: canViewCreditLimitMenu(user),
      nestedLinks: [
        {
          label: "CLIK Data",
          url: AppRoute.ClikData,
          show: hasPermission(user, RolePermission.CanViewClikData),
        },
        {
          label: "Risk Funnel Dashboard",
          url: AppRoute.RiskFunnelDashboard,
          show: hasPermission(
            user,
            RolePermission.CanViewRiskFunnelDashboard,
            true
          ),
        },
        {
          label: "Ongoing Process",
          show:
            hasPermission(
              user,
              RolePermission.CanViewCreditUnderwriting,
              true
            ) ||
            hasPermission(user, RolePermission.CanViewRiskReview, true) ||
            hasPermission(user, RolePermission.CanViewRiskMonitoring, true),
          nestedLinks: [
            {
              label: "Submission List",
              url: AppRoute.SubmissionList,
              show: hasPermission(
                user,
                RolePermission.CanViewCreditUnderwriting,
                true
              ),
            },
            {
              label: "Credit Underwriting",
              url: AppRoute.CreditUnderwriting,
              show: hasPermission(
                user,
                RolePermission.CanViewCreditUnderwriting,
                true
              ),
            },
            {
              label: "Risk Review",
              url: AppRoute.RiskReview,
              show: hasPermission(user, RolePermission.CanViewRiskReview, true),
            },
            {
              label: "Risk Monitoring",
              url: AppRoute.RiskMonitoring,
              show: hasPermission(
                user,
                RolePermission.CanViewRiskMonitoring,
                true
              ),
            },
          ],
        },
      ],
    },
    {
      label: "AI Automation",
      icon: <SvgAIIcon width={iconSize} height={iconSize} />,
      show: hasPermission(user, RolePermission.CanViewAiAutomation, true),
      nestedLinks: [
        {
          label: "Run Automation",
          url: AppRoute.RunAIAutomation,
        },
        {
          label: "Result Database",
          url: AppRoute.AIResult,
        },
      ],
    },
    {
      label: "AR Management",
      icon: <SvgFolder width={iconSize} height={iconSize} />,
      show:
        hasPermission(user, RolePermission.CanViewCollection) ||
        hasPermission(user, RolePermission.CanViewActivityManagement),
      nestedLinks: [
        {
          label: "Dashboard",
          url: AppRoute.CollectionDashboard,
          show: hasPermission(user, RolePermission.CanViewCollection),
        },
        {
          label: "Collection Management",
          url: AppRoute.ActivityManagement,
          show: hasPermission(user, RolePermission.CanViewActivityManagement),
        },
        {
          label: "Review & Approval",
          url: AppRoute.ReviewAndApproval,
          show: hasPermission(user, RolePermission.CanViewReviewApproval),
        },
        {
          label: "Late Fee Calculation",
          url: AppRoute.LateFeeCalculator,
          show: hasPermission(user, RolePermission.CanViewActivityManagement),
        },
        {
          label: "Desk Collection",
          nestedLinks: [
            {
              label: "Desk Collection Dashboard",
              url: AppRoute.DeskCollectionDashboard,
            },
            {
              label: "Desk Collection Tools",
              url: AppRoute.DeskCollectionTools,
            },
            {
              label: "Message Template",
              url: AppRoute.DeskCollectionMessageTemplate,
            },
          ],
        },
        {
          label: "Trade Insurances",
          url: AppRoute.TradeInsurance,
          show:
            hasPermission(user, RolePermission.CanViewCollection) ||
            hasPermission(user, RolePermission.CanViewActivityManagement),
        },
      ],
    },
    {
      label: "IDP & FD",
      icon: <SvgSearch width={iconSize} height={iconSize} />,
      nestedLinks: [
        {
          label: "Upload Document",
          url: AppRoute.IDPForgeryCheck,
        },
        {
          label: "Document Database",
          url: AppRoute.IDPDatabase,
        },
      ],
    },
    {
      label: "KYC",
      icon: <KYCIcon width={iconSize} height={iconSize} />,
      url: AppRoute.KYC,
    },
    {
      label: "Paymaster Analysis",
      icon: <SvgFile width={iconSize} height={iconSize} />,
      url: AppRoute.PaymasterAnalysis,
      show: hasPermission(user, RolePermission.CanViewPaymaster, true),
    },
    {
      label: "Employee Candidate CLIK",
      icon: <SvgUserScan width={iconSize} height={iconSize} />,
      url: AppRoute.EmployeeCandidate,
      show: hasPermission(user, RolePermission.CanViewEmployeeCandidateClik),
    },
  ];

  const getRenderedLink = useCallback(
    (link: NavigationLink, index: number) => {
      if (link.show === false) {
        return null;
      }

      if (renderer) {
        return renderer(link, index);
      }

      return (
        <SidebarItem
          key={index}
          item={link}
          isOpen={isOpen}
          onClick={onClick}
        />
      );
    },
    [isOpen, onClick, renderer]
  );

  return links.map(getRenderedLink);
}
